import {
  ARENA_H, ARENA_W, GRAVITY, INTERP_DELAY_MS, JUMP_BUFFER_MS, JUMP_VY, PHYS_STEP_SEC, SNAP_DIST, ST, TICK_MS,
  canJumpInState, canMoveInState,
} from './constants';

/**
 * Client side of the server-authoritative real-time brawl (SPEC_V141_GAMES.md §3):
 *  - TickClock      maps server ticks (`k`, 50 ms each) to the local clock;
 *  - SnapshotBuffer keeps snapshots and samples them at renderTime = serverNow − 100 ms
 *                   (positions lerped, discrete fields passed through, events released in order);
 *  - OwnPredictor   predicts the own fighter in 2D (§11.1): walking with the server speed formula,
 *                   jumps (vy 1050, gravity 2400, fixed 50 ms semi-implicit Euler like the server), one-way platforms (land only when falling onto
 *                   them, `drop` falls through) and the floor y = 0; smoothly corrects toward the
 *                   server (snap if the error > 120). Fighters pass through each other — the
 *                   prediction never blocks on bodies.
 * Snapshots carry no server timestamp, so "server now" is measured in tick-time:
 * k·50 ms, anchored by the earliest observed arrival of each tick.
 */

export const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export class TickClock {
  constructor({ tickMs = TICK_MS, resyncMs = 400 } = {}) {
    this.tickMs = tickMs;
    this.resyncMs = resyncMs;
    this.base = null; // local ms at which tick 0 "happened"
  }

  /** Feed the arrival of tick `k` at local time `localNow`. */
  observe(k, localNow) {
    const est = localNow - k * this.tickMs;
    if (this.base === null || Math.abs(est - this.base) > this.resyncMs) {
      this.base = est;
    } else if (est < this.base) {
      // a faster arrival → the previous estimate was late; move most of the way
      this.base += (est - this.base) * 0.5;
    } else {
      // slow drift toward later arrivals (clock skew / growing latency)
      this.base += (est - this.base) * 0.02;
    }
  }

  /** Current server time in tick-ms (k·tickMs units). */
  now(localNow) {
    return this.base === null ? 0 : localNow - this.base;
  }

  reset() { this.base = null; }
}

export class SnapshotBuffer {
  constructor({ delayMs = INTERP_DELAY_MS, max = 60, clock = new TickClock() } = {}) {
    this.delayMs = delayMs;
    this.max = max;
    this.clock = clock;
    this.snaps = [];
    this.drainedK = -Infinity;
  }

  reset() {
    this.snaps = [];
    this.drainedK = -Infinity;
    this.clock.reset();
  }

  /** Add a server snapshot `{t:"s", k, tm, f, p, it, br, tr, ev}`. */
  push(snap, localNow = Date.now()) {
    if (!snap || !Number.isFinite(Number(snap.k))) return;
    const k = Number(snap.k);
    const entry = { ...snap, k, t: k * this.clock.tickMs };
    const list = this.snaps;
    if (list.length && list[list.length - 1].k >= k) {
      if (list.some((s) => s.k === k)) return; // duplicate
      const idx = list.findIndex((s) => s.k > k);
      list.splice(idx, 0, entry); // late (out of order) packet
    } else {
      list.push(entry);
    }
    this.clock.observe(k, localNow);
    // trim old snapshots, but never ones whose events were not released yet
    while (list.length > this.max && list[0].k <= this.drainedK) list.shift();
    if (list.length > this.max * 4) list.splice(0, list.length - this.max * 4);
  }

  latest() {
    return this.snaps.length ? this.snaps[this.snaps.length - 1] : null;
  }

  renderTime(localNow = Date.now()) {
    return this.clock.now(localNow) - this.delayMs;
  }

  /** Interpolated frame at tick-time `rt`. */
  sample(rt) {
    const list = this.snaps;
    if (!list.length) return null;
    let a = list[0];
    let b = null;
    if (rt <= a.t) {
      b = null;
    } else {
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].t <= rt) a = list[i];
        else { b = list[i]; break; }
      }
    }
    const alpha = b ? clamp((rt - a.t) / (b.t - a.t || 1), 0, 1) : 0;
    const since = Math.max(0, rt - a.t);
    return interpolateSnapshots(a, b, alpha, since);
  }

  /** Events of snapshots whose time has come (≤ rt), each once, in tick order. */
  drainEvents(rt) {
    const out = [];
    for (const s of this.snaps) {
      if (s.k <= this.drainedK) continue;
      if (s.t > rt) break;
      if (Array.isArray(s.ev)) s.ev.forEach((e) => out.push({ ...e, k: s.k }));
      this.drainedK = s.k;
    }
    return out;
  }
}

const byKey = (list, key) => {
  const m = new Map();
  (list || []).forEach((o) => m.set(o[key], o));
  return m;
};

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

/** Teleports (Dive Strike, panel respawn) are not lerped across the screen. */
const TELEPORT_DIST = 220;

function lerpXY(a, b, alpha) {
  const ax = num(a.x);
  const ay = num(a.y);
  if (!b) return { x: ax, y: ay };
  const bx = num(b.x);
  const by = num(b.y);
  if (Math.abs(bx - ax) + Math.abs(by - ay) > TELEPORT_DIST) {
    return alpha < 0.5 ? { x: ax, y: ay } : { x: bx, y: by };
  }
  return { x: lerp(ax, bx, alpha), y: lerp(ay, by, alpha) };
}

/** a/b snapshots, alpha ∈ [0,1]; discrete fields from `a`, positions (x AND y) lerped. */
export function interpolateSnapshots(a, b, alpha, sinceMs = 0) {
  const bf = b ? byKey(b.f, 'i') : null;
  const bp = b ? byKey(b.p, 'id') : null;
  const bi = b ? byKey(b.it, 'id') : null;
  const fighters = (a.f || []).map((fa) => {
    const { x, y } = lerpXY(fa, bf && bf.get(fa.i), alpha);
    return { ...fa, x, y, sa: (Number(fa.sa) || 0) + sinceMs };
  });
  const projectiles = (a.p || []).map((pa) => ({ ...pa, ...lerpXY(pa, bp && bp.get(pa.id), alpha) }));
  const items = (a.it || []).map((ia) => ({ ...ia, ...lerpXY(ia, bi && bi.get(ia.id), alpha) }));
  const tm = b && Number.isFinite(Number(b.tm)) && Number.isFinite(Number(a.tm))
    ? lerp(Number(a.tm), Number(b.tm), alpha)
    : Number(a.tm);
  return {
    k: a.k,
    tm,
    f: fighters,
    p: projectiles,
    it: items,
    br: a.br || [],
    tr: a.tr || [],
  };
}

// ── 2D body physics (same rules as the server's BrawlEngine, §11.1) ──────────────────────────

const EPS = 0.5;

/** Platform under (x, y) when standing on it (|y − p.y| < EPS), else null. Floor is not a platform. */
export function platformAt(platforms, x, y) {
  if (!platforms) return null;
  return platforms.find((p) => Math.abs(p.y - y) < EPS && x >= p.x1 && x <= p.x2) || null;
}

/**
 * One body integration step — semi-implicit Euler exactly like the server tick:
 * vy −= g·dt first, then y += vy·dt; land on a platform only when falling onto it.
 * body  {x, y, vy, gr, ignoreY}  — ignoreY = platform level being dropped through
 * opts  {dir, speed, canMove, platforms, worldW, gravity}
 * → new body + `landed` flag
 */
export function stepBody(body, dt, {
  dir = 0, speed = 0, canMove = true, platforms = [], worldW = ARENA_W, gravity = GRAVITY,
} = {}) {
  let { x, y, vy, gr, ignoreY } = body;
  let landed = false;
  if (canMove && dir) x = clamp(x + dir * speed * dt, 0, worldW);
  if (gr) {
    if (y > EPS && !platformAt(platforms, x, y)) {
      gr = false; // walked off a ledge
      vy = 0;
    } else {
      return { x, y: y <= EPS ? 0 : y, vy: 0, gr: true, ignoreY: null, landed };
    }
  }
  const prevY = y;
  vy -= gravity * dt;
  y += vy * dt;
  if (vy <= 0) {
    let land = null;
    (platforms || []).forEach((p) => {
      if (ignoreY !== null && ignoreY !== undefined && Math.abs(p.y - ignoreY) < EPS) return;
      if (x < p.x1 || x > p.x2) return;
      if (prevY >= p.y - EPS && y <= p.y && (land === null || p.y > land)) land = p.y;
    });
    if (land === null && y <= 0) land = 0;
    if (land !== null) {
      y = land;
      vy = 0;
      gr = true;
      ignoreY = null;
      landed = true;
    }
  }
  if (!gr && ignoreY !== null && ignoreY !== undefined && y < ignoreY - 2) ignoreY = null;
  return { x, y, vy, gr, ignoreY: ignoreY ?? null, landed };
}

const lerpBody = (a, b, t) => ({
  ...a,
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  vy: lerp(a.vy, b.vy, t),
});

/**
 * Integrates `dtSec` in fixed server-sized steps (default 50 ms); the remainder is shown as a
 * linear blend toward the next step (exactly on the server's discrete trajectory, but smooth).
 */
export function simulate(body, dtSec, opts, step = PHYS_STEP_SEC) {
  let b = { ignoreY: null, ...body };
  let left = Math.max(0, dtSec);
  let landed = false;
  while (left >= step - 1e-9) {
    b = stepBody(b, step, opts);
    landed = landed || b.landed;
    left -= step;
  }
  if (left > 1e-6) {
    const n = stepBody(b, step, opts);
    return { ...lerpBody(b, n, left / step), gr: b.gr, ignoreY: b.ignoreY, landed };
  }
  return { ...b, landed };
}

/** Server fighter → body (`y`/`gr` default to the floor for the v1.41 protocol). */
export function serverBody(f, platforms) {
  const x = num(f.x);
  const y = num(f.y);
  const vy = num(f.vy);
  const gr = f.gr === undefined || f.gr === null ? y <= EPS : !!Number(f.gr);
  // at the very start of a drop the server is still at the platform level, but not grounded
  const ignore = !gr && vy <= 0 ? platformAt(platforms, x, y) : null;
  return { x, y, vy, gr, ignoreY: ignore ? ignore.y : null };
}

/**
 * Own fighter prediction (2D). Inputs are applied immediately: walking at the server speed,
 * `jump()` / `drop()` start the jump / fall-through locally. The body is integrated in the
 * server's fixed 50 ms steps (committed state + accumulator, rendered as a blend toward the next
 * step). The prediction is pulled toward the newest server state advanced by the input latency
 * ("lead"), snapped when the error > snapDist. Right after a local jump/drop the vertical
 * correction waits until the server confirms it (or `maxPendingSec` passes), so the jump is not
 * pulled back to the floor. A jump pressed in the air is buffered for 250 ms (fires on landing).
 */
export class OwnPredictor {
  constructor({
    speed = 260, worldW = ARENA_W, worldH = ARENA_H, platforms = [], gravity = GRAVITY, jumpVy = JUMP_VY,
    snapDist = SNAP_DIST, correctionRate = 8, deadzone = 1.5, maxLeadSec = 0.25, maxPendingSec = 0.4,
    step = PHYS_STEP_SEC, jumpBufferSec = JUMP_BUFFER_MS / 1000,
  } = {}) {
    this.speed = speed;
    this.worldW = worldW;
    this.worldH = worldH;
    this.platforms = platforms;
    this.gravity = gravity;
    this.jumpVy = jumpVy;
    this.snapDist = snapDist;
    this.correctionRate = correctionRate;
    this.deadzone = deadzone;
    this.maxLeadSec = maxLeadSec;
    this.maxPendingSec = maxPendingSec;
    this.step = step;
    this.jumpBufferSec = jumpBufferSec;
    this.input = { l: 0, r: 0, b: 0 };
    this.body = null; // committed state at a step boundary
    this.acc = 0; // time since the last committed step
    this.d = 1;
    this.st = ST.IDLE;
    this.time = 0;
    this.pending = null; // {kind: 'jump'|'drop', at}
    this.bufferedJumpAt = null;
  }

  /** x of the predicted body (null before the first server state). */
  get x() { return this.body ? this.body.x : null; }

  setInput({ l = 0, r = 0, b = 0 } = {}) {
    this.input = { l: l ? 1 : 0, r: r ? 1 : 0, b: b ? 1 : 0 };
  }

  setSpeed(speed) {
    if (Number(speed) > 0) this.speed = Number(speed);
  }

  setWorld({ w, h, platforms } = {}) {
    if (Number(w) > 0) this.worldW = Number(w);
    if (Number(h) > 0) this.worldH = Number(h);
    if (Array.isArray(platforms)) this.platforms = platforms;
  }

  /** reset() / reset(x) / reset({x, y, vy, gr}) */
  reset(state = null) {
    this.pending = null;
    this.bufferedJumpAt = null;
    this.acc = 0;
    if (state === null || state === undefined) {
      this.body = null;
    } else if (typeof state === 'number') {
      this.body = {
        x: state, y: 0, vy: 0, gr: true, ignoreY: null,
      };
    } else {
      this.body = {
        y: 0, vy: 0, gr: true, ignoreY: null, ...state,
      };
    }
  }

  direction() {
    return this.input.r - this.input.l;
  }

  opts(dir, slowed, canMove) {
    return {
      dir,
      speed: this.speed * (slowed ? 0.5 : 1),
      canMove,
      platforms: this.platforms,
      worldW: this.worldW,
      gravity: this.gravity,
    };
  }

  startJump() {
    this.body = { ...this.body, vy: this.jumpVy, gr: false, ignoreY: null };
    this.pending = { kind: 'jump', at: this.time };
    this.bufferedJumpAt = null;
  }

  /**
   * Local jump (from a support, in IDLE/WALK/ATK_RECOVER). In the air the press is buffered and
   * fires on landing within 250 ms. Returns true if a jump started now.
   */
  jump() {
    const b = this.body;
    if (!b || this.input.b || !canJumpInState(this.st)) return false;
    if (!b.gr) {
      this.bufferedJumpAt = this.time;
      return false;
    }
    this.startJump();
    return true;
  }

  /** Local drop through the platform under the fighter (not the floor). */
  drop() {
    const b = this.body;
    if (!b || !b.gr || b.y <= EPS || !canJumpInState(this.st)) return false;
    const p = platformAt(this.platforms, b.x, b.y);
    if (!p) return false;
    this.body = { ...b, vy: 0, gr: false, ignoreY: p.y };
    this.pending = { kind: 'drop', at: this.time };
    return true;
  }

  /**
   * @param dtSec   seconds since the previous update
   * @param server  own fighter from the newest snapshot `{x, y, vy, gr, d, st, bf}`
   * @param leadSec input latency estimate (RTT/2 + snapshot age), clamped to maxLeadSec
   * @returns {{x, y, vy, gr, d, landed}} or null without a server state
   */
  update(dtSec, server, leadSec = 0) {
    if (!server) return null;
    const srv = serverBody(server, this.platforms);
    if (!this.body || !Number.isFinite(this.body.x)) {
      this.body = { ...srv };
      this.acc = 0;
    }
    this.st = server.st;
    const dt = clamp(Number(dtSec) || 0, 0, 0.1);
    this.time += dt;
    const dir = this.direction();
    const slowed = Array.isArray(server.bf) && server.bf.includes('SLOW');
    const canMove = !this.input.b && canMoveInState(server.st);
    const moving = dir !== 0 && canMove;
    const opts = this.opts(dir, slowed, canMove);

    // 1. own motion in fixed server steps
    let landed = false;
    this.acc += dt;
    while (this.acc >= this.step - 1e-9) {
      const wasAir = !this.body.gr;
      this.body = stepBody(this.body, this.step, opts);
      this.acc -= this.step;
      if (wasAir && this.body.gr) {
        landed = true;
        if (this.bufferedJumpAt !== null && this.time - this.bufferedJumpAt <= this.jumpBufferSec
          && canJumpInState(server.st) && !this.input.b) {
          this.startJump();
        }
      }
    }
    if (this.bufferedJumpAt !== null && this.time - this.bufferedJumpAt > this.jumpBufferSec) this.bufferedJumpAt = null;
    this.acc = Math.max(0, this.acc);

    // 2. server target, advanced by the latency (walking or airborne)
    const lead = clamp(leadSec, 0, this.maxLeadSec);
    const leadDir = moving && (server.st === ST.WALK || !srv.gr) ? dir : 0;
    const target = lead > 0 && (leadDir !== 0 || !srv.gr)
      ? simulate(srv, lead, this.opts(leadDir, slowed, canMove), this.step)
      : srv;

    // 3. pending local jump/drop → wait for the server before correcting y
    if (this.pending) {
      const confirmed = this.pending.kind === 'jump' ? (!srv.gr && srv.vy > 0) : !srv.gr;
      if (confirmed || this.time - this.pending.at > this.maxPendingSec) this.pending = null;
    }
    const holdY = !!this.pending;

    const b = this.body;
    const ex = target.x - b.x;
    const ey = holdY ? 0 : target.y - b.y;
    if (Math.hypot(ex, ey) > this.snapDist) {
      this.body = holdY
        ? { ...b, x: target.x }
        : {
          x: target.x, y: target.y, vy: target.vy, gr: target.gr, ignoreY: target.ignoreY ?? null,
        };
    } else {
      const k = Math.min(1, dt * this.correctionRate);
      const nb = { ...b };
      if (Math.abs(ex) > this.deadzone) nb.x += ex * k;
      if (!holdY) {
        if (target.gr && !nb.gr && Math.abs(target.y - nb.y) < 8 && nb.vy <= 0) {
          nb.y = target.y; // touch down together with the server
          nb.vy = 0;
          nb.gr = true;
          nb.ignoreY = null;
        } else if (!target.gr && nb.gr && Math.abs(target.y - nb.y) > 8) {
          nb.gr = false; // launched / knocked off by the server
          nb.vy = target.vy;
          nb.ignoreY = target.ignoreY ?? null;
          nb.y += ey * k;
        } else if (target.gr && nb.gr) {
          if (Math.abs(target.y - nb.y) < 8) nb.y = target.y;
        } else if (Math.abs(ey) > this.deadzone) {
          nb.y += ey * k;
        }
      }
      this.body = nb;
    }
    this.d = Number(server.d) || this.d;

    // 4. rendered state: committed step blended toward the next one
    const shown = this.acc > 1e-6
      ? lerpBody(this.body, stepBody(this.body, this.step, opts), this.acc / this.step)
      : this.body;
    return {
      x: shown.x, y: shown.y, vy: shown.vy, gr: this.body.gr ? 1 : 0, d: this.d, landed,
    };
  }
}

/**
 * Glue for the renderer: one buffer + own predictor.
 * `frame(localNow)` → interpolated frame with the own fighter predicted (discrete fields of the
 * own fighter come from the newest snapshot for responsiveness). The own fighter faces the
 * nearest enemy by x (fighting-game convention, same as the server), using the predicted x.
 */
export function createBrawlEngine({
  you = 0, speed = 260, worldW = ARENA_W, worldH = ARENA_H, platforms = [], delayMs = INTERP_DELAY_MS,
} = {}) {
  const buffer = new SnapshotBuffer({ delayMs });
  const predictor = new OwnPredictor({
    speed, worldW, worldH, platforms,
  });
  let lastFrameAt = null;
  let lastArrival = null;
  let rttMs = 100;
  let teams = null; // Map idx → team
  const engine = {
    you,
    world: { w: worldW, h: worldH, platforms },
    buffer,
    predictor,
    push(snap, localNow = Date.now()) {
      buffer.push(snap, localNow);
      lastArrival = localNow;
    },
    setInput(input) { predictor.setInput(input); },
    /** Local jump prediction; returns true when predicted (the act is sent regardless). */
    jump() { return predictor.jump(); },
    drop() { return predictor.drop(); },
    setRtt(ms) { if (Number(ms) > 0) rttMs = Number(ms); },
    setYou(idx) { engine.you = idx; },
    setWorld(world) {
      if (!world) return;
      engine.world = { ...engine.world, ...world };
      predictor.setWorld(engine.world);
    },
    /** fighters meta [{idx, team}] — to know who is an enemy (default: everybody else). */
    setFighters(list) {
      teams = new Map();
      (list || []).forEach((f) => teams.set(f.idx, f.team));
    },
    reset() {
      buffer.reset();
      predictor.reset(null);
      lastFrameAt = null;
      lastArrival = null;
    },
    latest() { return buffer.latest(); },
    frame(localNow = Date.now()) {
      const rt = buffer.renderTime(localNow);
      const frame = buffer.sample(rt);
      if (!frame) return null;
      const dt = lastFrameAt === null ? 0 : (localNow - lastFrameAt) / 1000;
      lastFrameAt = localNow;
      const newest = buffer.latest();
      const own = newest && (newest.f || []).find((f) => f.i === engine.you);
      if (own) {
        const age = lastArrival === null ? 0 : localNow - lastArrival;
        const pred = predictor.update(dt, own, (rttMs / 2 + age) / 1000);
        const idx = frame.f.findIndex((f) => f.i === engine.you);
        let { d } = pred;
        const myTeam = teams ? teams.get(engine.you) : undefined;
        let best = null;
        frame.f.forEach((f) => {
          if (f.i === engine.you || f.st === ST.DEAD || Number(f.hp) <= 0) return;
          if (teams && myTeam !== undefined && teams.get(f.i) === myTeam) return;
          const dx = Number(f.x) - pred.x;
          if (best === null || Math.abs(dx) < Math.abs(best)) best = dx;
        });
        if (best !== null && Math.abs(best) > 6) d = best > 0 ? 1 : -1;
        const merged = {
          ...own,
          x: pred.x,
          y: pred.y,
          vy: pred.vy,
          gr: pred.gr,
          d,
          sa: (Number(own.sa) || 0) + age,
        };
        if (idx >= 0) frame.f[idx] = merged; else frame.f.push(merged);
      }
      frame.rt = rt;
      return frame;
    },
    events(localNow = Date.now()) {
      return buffer.drainEvents(buffer.renderTime(localNow));
    },
  };
  return engine;
}
